<template>
  <v-container fluid>
    <div v-if="user.can('view_sustainability')">
      <div class="d-flex justify-space-between align-center">
        <h2 class="mb-0">
          {{ $t('sustainability_dashboard' )}}
        </h2>
        <sustainability-settings/>
      </div>
      <v-row>
        <v-col>
          <h4>
            <router-link to="/sustainability" v-if="singleCustomerSelected">{{ $t('all_dcs') }}</router-link> <span v-if="singleCustomerSelected"> / {{ selectedCustomer.name }}</span>
            <span v-else>{{ $t('all_dcs') }}</span>
            <span class="ml-2 text-muted">({{ this.summaryString }})</span>
          </h4>
        </v-col>
        <v-col>
          <div class="float-right">
            <h4><span v-if="vehicleData" class="small ml-2 text-muted">{{ $t('sustainability_dashboard_ui.vehicles_analyzed_summary', { analyzed: vehicleData.totalAnalyzedVehicles, total: vehicleData.totalCustomerVehicles })}} ({{ vehicleData.totalVehiclePercentage }}%)</span></h4>
            <h4><span v-if="vehicleData" class="small ml-2 text-muted">{{ $t('sustainability_dashboard_ui.routes_analyzed_summary', { analyzed: vehicleData.totalAnalyzedRoutes, total: vehicleData.totalCustomerRoutes })}} ({{ vehicleData.totalRoutePercentage }}%)</span></h4>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col xl="4">
          <!-- <sustainability-score :sustainability-score="100" :loading="loading" :singleCustomerSelected="singleCustomerSelected" :selectedCustomer="selectedCustomer"/> -->
          <distribution-center-summary :a-vehicles="vehicleData.analyzedVehicles" :c-vehicles="vehicleData.customerVehicles" :a-routes="vehicleData.analyzedRoutes" :c-routes="vehicleData.customerRoutes" :loading="loading"/>
        </v-col>
        <v-col xl="8">
          <staging-reefer-activity :sustainability-customers="expandedCustomers" :metrics-data="stagingMetrics" :staging-runtime-metrics-data="stagingRuntimeMetrics" :loading="loading"/>
          <enroute-reefer-activity :metrics-data="enrouteMetrics" :loading="loading"/>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import StagingReeferActivity from '@/components/sustainability/dashboard/StagingReeferActivity'
import EnrouteReeferActivity from '@/components/sustainability/dashboard/EnrouteReeferActivity'
import SustainabilityScore from '@/components/sustainability/dashboard/SustainabilityScore'
import DistributionCenterSummary from '@/components/sustainability/dashboard/DistributionCenterSummary'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import SustainabilityMixin from '@/components/mixins/SustainabilityMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import SustainabilitySettings from '@/components/sustainability/SustainabilitySettings'
import MathUtil from '@/util/MathUtil'
import Moment from 'moment-timezone'
import { map, filter, reduce, union, keys, first, values, sumBy, get } from 'lodash'

export default {
  name: 'SustainabilityDashboard',
  components: {
    StagingReeferActivity,
    EnrouteReeferActivity,
    SustainabilityScore,
    DistributionCenterSummary,
    SustainabilitySettings
  },
  mixins: [
    CurrentUserMixin,
    SustainabilityMixin,
    ApiUtilitiesMixin
  ],
  data () {
    return {
      expandedCustomers: [],
      loading: null,
      stagingMetrics: null,
      enrouteMetrics: null,
      stagingRuntimeMetrics: null,
      vehicleData: {
        totalAnalyzedVehicles: null,
        totalCustomerVehicles: null,
        analyzedVehicles: null,
        customerVehicles: null,
        totalVehiclePercentage: null
      }
    }
  },
  mounted () {
    this.loadData()
  },
  computed: {
    /**
     * Whether a single customer is currently selected.
     */
    singleCustomerSelected () {
      return keys(this.selectedCustomers).length === 1
    },
    /**
     * The selected customer, if a single customer is currently selected.
     */
    selectedCustomer () {
      if (this.singleCustomerSelected) {
        return first(values(this.selectedCustomers))
      }
      return null
    },
    /**
     * Calculate the sustainability score from the staging and enroute metrics.
     * @returns {null|number|*}
     */
    sustainabilityScore () {
      if (this.stagingMetrics && this.enrouteMetrics) {
        const stagingValues = values(this.stagingMetrics)
        const enrouteValues = values(this.enrouteMetrics)

        const targetStagingDiesel = sumBy(stagingValues, x => get(x, 'diesel.power_diesel_total_target_l', 0))
        const targetEnrouteDiesel = sumBy(enrouteValues, x => get(x, 'power_diesel_target_l', 0))

        const totalStagingDiesel = sumBy(stagingValues, x => get(x, 'diesel.power_diesel_l', 0))
        const totalEnrouteDiesel = sumBy(enrouteValues, x => get(x, 'power_diesel_l', 0))
        const shorePowerTargetMissDiesel = sumBy(stagingValues, x => get(x, 'shore.shore_power_target_miss_l', 0))

        const target = targetStagingDiesel + targetEnrouteDiesel
        const actual = totalStagingDiesel + totalEnrouteDiesel + shorePowerTargetMissDiesel

        if (actual > 0) {
          return MathUtil.percentage(target, actual, 1)
        }
      }
      return null
    },
    /**
     * Computed property to back the currently selected customers with the route customerIds param. This way, every time
     * the selected customers changes, the url will change, and vice versa. This will let users use the back/forward
     * buttons to jump through various searches to compare. It also lets the user back out of viewing a single customer.
     */
    selectedCustomers: {
      get () {
        const param = this.$route.params.customerIds
        // If no customers specified, default to the user's top-level customers.
        if (param == null) {
          return filter(this.customers, x => x.isTopLevel())
        }
        const split = param.split(',')
        return filter(this.customers, x => split.includes(x.id.toString()))
      }
    }
  },
  methods: {
    /**
     * Load the data from the API
     */
    async loadData () {
      this.loading = true
      try {
        // We want to expand the customer ids of any parent customers in the list, but not double count them if any of
        // the children are also present. Expand each customer if possible and merge in any children via union.
        this.expandedCustomers = await this.getExpandedCustomers(this.selectedCustomers, this.customers)
        const expandedCustomerIds = union(map(this.expandedCustomers, x => x.id))
        // Set the start and end date depending on whether a custom date range has been selected or not.
        // If not, then the value of timeRange should be a relative date span option, eg '-30 days' or '-1 years' that
        // will be parseable by the API
        let startDate, endDate
        if (this.sustainabilityParams.timeRange !== 'custom') {
          startDate = this.sustainabilityParams.timeRange
          endDate = 'now'
        } else {
          startDate = Moment.tz(this.sustainabilityParams.startDate, this.customerTimezone).format()
          endDate = Moment.tz(this.sustainabilityParams.endDate, this.customerTimezone).set({ hour: 23, minute: 59, second: 59 }).format()
        }
        const vehicleDataResult = await this.$httpAdapter.get('/sustainability-insights/vehicle-data', {
          params: {
            customer_id: expandedCustomerIds.join(','),
            start: startDate,
            end: endDate
          }
        })
        this.vehicleData = vehicleDataResult.data
        const stagingMetricsResult = await this.$httpAdapter.get('/sustainability-insights/staging-metrics', {
          params: {
            customer_id: expandedCustomerIds.join(','),
            start: startDate,
            end: endDate,
            gallons_per_hour: this.sustainabilityParams.gallonsPerHour
          }
        })
        this.stagingMetrics = stagingMetricsResult.data
        const enrouteMetricsResult = await this.$httpAdapter.get('/sustainability-insights/enroute-metrics', {
          params: {
            customer_id: expandedCustomerIds.join(','),
            start: startDate,
            end: endDate,
            dieselPower: this.sustainabilityParams.dieselRuntimeTarget
          }
        })
        this.enrouteMetrics = enrouteMetricsResult.data
        const stagingRuntimeMetricsResult = await this.$httpAdapter.get('/sustainability-insights/staging-runtime-metrics', {
          params: {
            customer_id: expandedCustomerIds.join(','),
            start: startDate,
            end: endDate,
            stagingRuntimeTarget: this.sustainabilityParams.stagingRuntimeTarget
          }
        })
        this.stagingRuntimeMetrics = stagingRuntimeMetricsResult.data
      } catch (error) {
        this.renderHttpAdapterErrorBanner(error)
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    selectedCustomers () {
      this.loadData()
    },
    sustainabilityParams (newValue, oldValue) {
      // Only need to reload the data if the time range changed
      if (
        newValue.timeRange !== oldValue.timeRange ||
        newValue.startDate !== oldValue.startDate ||
        newValue.endDate !== oldValue.endDate ||
        newValue.gallonsPerHour !== oldValue.gallonsPerHour ||
        newValue.dieselRuntimeTarget !== oldValue.dieselRuntimeTarget ||
        newValue.dieselPrice !== oldValue.dieselPrice ||
        newValue.gallonsPerHour !== oldValue.gallonsPerHour ||
        newValue.stagingRuntimeTarget !== oldValue.stagingRuntimeTarget ||
        newValue.dieselRuntimeTarget !== oldValue.dieselRuntimeTarget
      ) {
        this.loadData()
      }
    }
  }
}
</script>

<style lang="scss">

.card-body {
  padding: 1rem;
}
.dashboard-card {
  min-height: 10rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
}
.dashboard-card hr {
  background-color: gray;
}
.card-header {
  font-weight: bold;
  font-size: 1.2rem;
  display: flex;
  justify-content: space-between;
}
.card-stats {
  display: flex;
  justify-content: space-around;
  font-size: 1.4rem;
  font-weight: 600;
  color: gray;
}
.target-score-label {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: gray;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.2rem;
}
.target-score {
  font-size: 2rem;
  font-weight: 600;
}
.dc-summary-label {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25rem;
}
.dc-summary-score {
  font-size: .75rem;
  line-height: 1rem;
  font-weight: 600;
  color: gray;
}
@media screen and (max-width: 1450px) {
  .card-stats {
    font-size: 1.2rem;
  }
}
</style>

<style scoped lang="scss">
</style>
